import { useMemo, useState } from 'react';
import { useQuery } from 'urql';
import PageHeader from '@/components/PageHeader.jsx';
import { REPORTE_MAYOR, REPORTE_SUMAS_SALDOS, REPORTE_BALANCE } from './gql.js';
import { LIST_CUENTAS } from '../Cuentas/gql.js';
import { LIST_CENTROS } from '../Centros/gql.js';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';

function ExportButtons({ onExport }) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onExport?.('csv')}
        className="rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
      >
        Exportar CSV
      </button>
      <button
        type="button"
        onClick={() => onExport?.('xlsx')}
        className="rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
      >
        Exportar XLSX
      </button>
      <button
        type="button"
        onClick={() => onExport?.('pdf')}
        className="rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
      >
        Exportar PDF
      </button>
    </div>
  );
}

export default function ReportesPage() {
  const toast = useToast();
  const [empresaId, setEmpresaId] = useState('1');
  const [periodo, setPeriodo] = useState(new Date().toISOString().slice(0, 7));
  const [cuentaId, setCuentaId] = useState('');
  const [desde, setDesde] = useState(new Date().toISOString().slice(0, 10));
  const [hasta, setHasta] = useState(new Date().toISOString().slice(0, 10));
  const [centroId, setCentroId] = useState('');

  const [cuentasResult] = useQuery({ query: LIST_CUENTAS, variables: { limit: 500, offset: 0 } });
  const [centrosResult] = useQuery({ query: LIST_CENTROS, variables: { empresaId }, pause: !empresaId });

  const mayorVariables = cuentaId
    ? { cuentaId, desde, hasta, centroId: centroId || null }
    : null;
  const [{ data: mayorData, fetching: mayorLoading }] = useQuery({
    query: REPORTE_MAYOR,
    variables: mayorVariables ?? {},
    pause: !mayorVariables,
  });

  const [{ data: sumasData, fetching: sumasLoading }] = useQuery({
    query: REPORTE_SUMAS_SALDOS,
    variables: { empresaId, periodo },
    pause: !empresaId || !periodo,
  });
  const [{ data: balanceData, fetching: balanceLoading }] = useQuery({
    query: REPORTE_BALANCE,
    variables: { empresaId, periodo },
    pause: !empresaId || !periodo,
  });

  const cuentas = cuentasResult.data?.listCuentas?.items ?? [];
  const centros = useMemo(() => flattenCentros(centrosResult.data?.listCentros ?? []), [centrosResult.data]);

  const handleExport = (tipo, dataset, nombre) => {
    if (!dataset) {
      toast.error('No hay datos para exportar.');
      return;
    }
    if (tipo === 'csv') {
      try {
        const { headers, rows, filename } = prepareExport(nombre, dataset);
        const csv = buildCsv(headers, rows);
        downloadFile(csv, `${filename}-${buildTimestamp()}.csv`, 'text/csv;charset=utf-8;');
        toast.success('Exportación CSV generada.');
      } catch (error) {
        toast.error(error.message ?? 'No pudimos exportar el reporte.');
      }
      return;
    }
    const label = tipo.toUpperCase();
    toast.info(`${label} estará disponible próximamente. Usá el CSV como alternativa.`);
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),0.95)] pb-16">
      <div className="mx-auto max-w-[1400px] px-4 pb-10 pt-8">
        <PageHeader
          title="Reportes Contables"
          subtitle="Consultá mayores, sumas y saldos, y balance de comprobación"
        />

        <div className="grid grid-cols-12 gap-6">
          <section className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-6 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
            <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Mayor de cuenta</h2>
                <p className="text-sm text-[rgb(var(--muted))]">Seleccione una cuenta para ver los movimientos detallados.</p>
              </div>
              <ExportButtons
                onExport={(tipo) => handleExport(tipo, mayorData?.mayor, 'mayor')}
              />
            </header>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-4">
                <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Cuenta</label>
                <select
                  value={cuentaId}
                  onChange={(event) => setCuentaId(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                >
                  <option value="">Seleccioná cuenta</option>
                  {cuentas.map((cuenta) => (
                    <option key={cuenta.id} value={cuenta.id}>
                      {cuenta.codigo} · {cuenta.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-6 md:col-span-4">
                <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Desde</label>
                <input
                  type="date"
                  value={desde}
                  onChange={(event) => setDesde(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-4">
                <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Hasta</label>
                <input
                  type="date"
                  value={hasta}
                  onChange={(event) => setHasta(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Centro</label>
                <select
                  value={centroId}
                  onChange={(event) => setCentroId(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                >
                  <option value="">Todos</option>
                  {centros.map((centro) => (
                    <option key={centro.id} value={centro.id}>
                      {centro.codigo} · {centro.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)]">
              {mayorLoading ? (
                <div className="px-4 py-6 text-sm text-[rgb(var(--muted))]">Cargando mayor...</div>
              ) : mayorData?.mayor ? (
                <MayorTable mayor={mayorData.mayor} />
              ) : (
                <div className="px-4 py-6 text-sm text-[rgb(var(--muted))]">Seleccioná una cuenta y rango de fechas para ver el mayor.</div>
              )}
            </div>
          </section>

          <section className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-6 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
            <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Sumas y saldos</h2>
                <p className="text-sm text-[rgb(var(--muted))]">Consolidado por cuenta para el período seleccionado.</p>
              </div>
              <ExportButtons onExport={(tipo) => handleExport(tipo, sumasData?.sumasSaldos, 'sumas-saldos')} />
            </header>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-4">
                <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Empresa</label>
                <input
                  type="number"
                  min="1"
                  value={empresaId}
                  onChange={(event) => setEmpresaId(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <label className="text-xs font-medium uppercase text-[rgb(var(--muted))]">Período</label>
                <input
                  value={periodo}
                  onChange={(event) => setPeriodo(event.target.value)}
                  placeholder="YYYY-MM"
                  className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                />
              </div>
            </div>
            <div className="mt-6 overflow-x-auto rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)]">
              {sumasLoading ? (
                <div className="px-4 py-6 text-sm text-[rgb(var(--muted))]">Cargando sumas y saldos...</div>
              ) : sumasData?.sumasSaldos?.length ? (
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="border-b border-[rgb(var(--border))] text-[rgb(var(--muted))]">
                    <tr>
                      <th className="px-4 py-2 text-left">Cuenta</th>
                      <th className="px-4 py-2 text-left">Tipo</th>
                      <th className="px-4 py-2 text-right">Debe</th>
                      <th className="px-4 py-2 text-right">Haber</th>
                      <th className="px-4 py-2 text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--border))]">
                    {sumasData.sumasSaldos.map((fila) => (
                      <tr key={fila.cuentaId}>
                        <td className="px-4 py-2 font-medium text-[rgb(var(--fg))]">
                          {fila.cuentaCodigo} · {fila.cuentaNombre}
                        </td>
                        <td className="px-4 py-2 text-[rgb(var(--muted))]">{fila.tipo}</td>
                        <td className="px-4 py-2 text-right">{Number(fila.debe ?? 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">{Number(fila.haber ?? 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">{Number(fila.saldo ?? 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-4 py-6 text-sm text-[rgb(var(--muted))]">No hay información disponible para el período.</div>
              )}
            </div>
          </section>

          <section className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-6 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
            <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Balance de comprobación</h2>
                <p className="text-sm text-[rgb(var(--muted))]">Totales por tipo de cuenta para el período seleccionado.</p>
              </div>
              <ExportButtons onExport={(tipo) => handleExport(tipo, balanceData?.balanceComprobacion, 'balance')} />
            </header>
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)]">
              {balanceLoading ? (
                <div className="px-4 py-6 text-sm text-[rgb(var(--muted))]">Cargando balance...</div>
              ) : balanceData?.balanceComprobacion?.length ? (
                <table className="w-full min-w-[520px] text-sm">
                  <thead className="border-b border-[rgb(var(--border))] text-[rgb(var(--muted))]">
                    <tr>
                      <th className="px-4 py-2 text-left">Código</th>
                      <th className="px-4 py-2 text-left">Nombre</th>
                      <th className="px-4 py-2 text-left">Tipo</th>
                      <th className="px-4 py-2 text-right">Debe</th>
                      <th className="px-4 py-2 text-right">Haber</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--border))]">
                    {balanceData.balanceComprobacion.map((fila) => (
                      <tr key={`${fila.tipo}-${fila.codigo}`}>
                        <td className="px-4 py-2 font-medium text-[rgb(var(--fg))]">{fila.codigo}</td>
                        <td className="px-4 py-2">{fila.nombre}</td>
                        <td className="px-4 py-2 text-[rgb(var(--muted))]">{fila.tipo}</td>
                        <td className="px-4 py-2 text-right">{Number(fila.saldoDebe ?? 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">{Number(fila.saldoHaber ?? 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-4 py-6 text-sm text-[rgb(var(--muted))]">No hay resultados para el balance.</div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function MayorTable({ mayor }) {
  return (
    <div className="overflow-x-auto">
      <div className="px-4 py-3 text-sm text-[rgb(var(--muted))]">
        Saldo inicial: <strong className="text-[rgb(var(--fg))]">{mayor.saldoInicial.toFixed(2)}</strong> · Saldo final: <strong className="text-[rgb(var(--fg))]">{mayor.saldoFinal.toFixed(2)}</strong>
      </div>
      <table className="w-full min-w-[640px] text-sm">
        <thead className="border-b border-[rgb(var(--border))] text-[rgb(var(--muted))]">
          <tr>
            <th className="px-4 py-2 text-left">Fecha</th>
            <th className="px-4 py-2 text-left">Asiento</th>
            <th className="px-4 py-2 text-left">Descripción</th>
            <th className="px-4 py-2 text-right">Debe</th>
            <th className="px-4 py-2 text-right">Haber</th>
            <th className="px-4 py-2 text-right">Saldo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[rgb(var(--border))]">
          {mayor.movimientos.map((mov) => (
            <tr key={`${mov.asientoId}-${mov.descripcion}-${mov.fecha}`}>
              <td className="px-4 py-2">{mov.fecha}</td>
              <td className="px-4 py-2 text-[rgb(var(--muted))]">#{mov.asientoNumero}</td>
              <td className="px-4 py-2">{mov.glosa || mov.descripcion || '—'}</td>
              <td className="px-4 py-2 text-right">{Number(mov.debe ?? 0).toFixed(2)}</td>
              <td className="px-4 py-2 text-right">{Number(mov.haber ?? 0).toFixed(2)}</td>
              <td className="px-4 py-2 text-right">{Number(mov.saldo ?? 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function flattenCentros(nodes, acc = []) {
  for (const node of nodes) {
    acc.push({ id: node.id, codigo: node.codigo, nombre: node.nombre });
    if (node.hijos?.length) {
      flattenCentros(node.hijos, acc);
    }
  }
  return acc;
}

function prepareExport(nombre, dataset) {
  if (nombre === 'mayor') {
    const headers = ['Fecha', 'Asiento', 'Glosa', 'Descripción', 'Debe', 'Haber', 'Saldo acumulado'];
    const movimientos = dataset.movimientos ?? [];
    const rows = [
      ['Saldo inicial', '', '', '', '', '', formatNumber(dataset.saldoInicial)],
      ...movimientos.map((mov) => [
        mov.fecha,
        `#${mov.asientoNumero}`,
        mov.glosa ?? '',
        mov.descripcion ?? '',
        formatNumber(mov.debe),
        formatNumber(mov.haber),
        formatNumber(mov.saldo),
      ]),
      ['Saldo final', '', '', '', '', '', formatNumber(dataset.saldoFinal)],
    ];
    const codigo = dataset.cuentaCodigo ?? dataset.cuentaId ?? 'cuenta';
    return { headers, rows, filename: `reporte-mayor-${codigo}` };
  }
  if (nombre === 'sumas-saldos') {
    const headers = ['Cuenta', 'Nombre', 'Tipo', 'Debe', 'Haber', 'Saldo'];
    const rows = (dataset ?? []).map((fila) => [
      fila.cuentaCodigo,
      fila.cuentaNombre,
      fila.tipo,
      formatNumber(fila.debe),
      formatNumber(fila.haber),
      formatNumber(fila.saldo),
    ]);
    return { headers, rows, filename: 'sumas-saldos' };
  }
  if (nombre === 'balance') {
    const headers = ['Código', 'Nombre', 'Tipo', 'Debe', 'Haber'];
    const rows = (dataset ?? []).map((fila) => [
      fila.codigo,
      fila.nombre,
      fila.tipo,
      formatNumber(fila.saldoDebe),
      formatNumber(fila.saldoHaber),
    ]);
    return { headers, rows, filename: 'balance-comprobacion' };
  }
  throw new Error('Tipo de reporte no soportado para exportación.');
}

function buildCsv(headers, rows) {
  const escapeCell = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };
  const lines = [headers.map(escapeCell).join(',')];
  for (const row of rows) {
    lines.push(row.map(escapeCell).join(','));
  }
  return `${lines.join('\n')}\n`;
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function buildTimestamp() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

function formatNumber(value) {
  return Number(value ?? 0).toFixed(2);
}
